import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { useAiRunStore } from '../store/aiRunStore';
import { socketService } from './socket';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.scicampus.com/api/v1';
const BASE_URL = API_BASE_URL;
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Request interceptor: attach bearer token ──────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Refresh token queue ───────────────────────────────────────────────────
// Ensures only one refresh request runs at a time. Concurrent 401s are
// queued and replayed once the single refresh completes.
let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

function notifyPending(token: string) {
  pendingRequests.forEach((cb) => cb(token));
  pendingRequests = [];
}

// ── Response interceptor: handle 401 → refresh → retry ───────────────────
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If a refresh is already in-flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingRequests.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest._retry = true;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // No body needed — the browser sends the httpOnly refreshToken cookie
        // automatically via withCredentials.
        const res = await axios.post(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const { accessToken: newToken } = (res.data as { data: { accessToken: string } }).data;
        useAuthStore.getState().setTokens(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        notifyPending(newToken);
        return api(originalRequest);
      } catch {
        pendingRequests = [];
        socketService.disconnect();
        // ai-run-storage is a separate localStorage key from auth-storage and
        // would otherwise survive this forced reload, leaking a stale/foreign
        // job into whichever session starts next on this browser.
        useAiRunStore.getState().clearActiveDetectorJob();
        useAuthStore.getState().logout();
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
